import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import './Login.css'; // We'll reuse the Login CSS

const STRONG_PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/;

const CreatePassword = () => {
  const { currentUser, setFirstTimePassword } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // If not in first-time login, redirect to appropriate dashboard
  React.useEffect(() => {
    if (currentUser && currentUser.isFirstLogin !== true) {
      redirectToDashboard(currentUser.userType, currentUser.requiresModeSelection);
    }
  }, [currentUser]);

  const redirectToDashboard = (userType, requiresModeSelection = false) => {
    if (requiresModeSelection) { navigate('/choose-access'); return; }
    switch (userType) {
      case 'admin':
        navigate('/admin/dashboard');
        break;
      case 'employee':
        navigate('/employee/calendar');
        break;
      case 'customer':
        navigate('/customer/dashboard');
        break;
      case 'support':
        navigate('/support');
        break;
      default:
        setError(`Unknown user type: ${userType}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Password validation
    if (!formData.password) {
      newErrors.password = t('auth.login.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.reset.tooShort');
    } else if (!STRONG_PASSWORD_RE.test(formData.password)) {
      newErrors.password = t('employee.profile.validation.passwordComplexity');
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.reset.mismatch');
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Call the API to set the new password
      const user = await setFirstTimePassword(formData.password);
      
      // Redirect based on user type
      redirectToDashboard(user.userType, user.requiresModeSelection);
    } catch (err) {
      setError(err.message || 'Failed to set password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <h1 className="login-title">{t('auth.createPassword.title')}</h1>
      <p className="login-subtitle">{t('auth.createPassword.instructions')}</p>
      
      {error && (
        <div className="login-alert">
          {error}
        </div>
      )}
      
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="password" className="form-label">{t('auth.createPassword.newPassword')}</label>
          <input
            type="password"
            id="password"
            name="password"
            className={`form-input ${errors.password ? 'input-error' : ''}`}
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your new password"
            autoComplete="new-password"
            aria-describedby="password-requirements"
          />
          {/* Keep the exact allow-list visible before submission. The password
              policy intentionally accepts only this punctuation subset, so a
              generic "special character" hint would reject otherwise sensible
              choices such as a hyphen or question mark without explanation. */}
          <div id="password-requirements" className="password-requirements">
            {t('auth.createPassword.requirements')}
          </div>
          {errors.password && <div className="error-text">{errors.password}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="confirmPassword" className="form-label">{t('auth.createPassword.confirmPassword')}</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            className={`form-input ${errors.confirmPassword ? 'input-error' : ''}`}
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your password"
            autoComplete="new-password"
          />
          {errors.confirmPassword && <div className="error-text">{errors.confirmPassword}</div>}
        </div>
        
        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('auth.createPassword.creating') : t('auth.createPassword.create')}
        </button>
      </form>
    </div>
  );
};

export default CreatePassword;
